'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  activateBusinessReportSource,
  createBusinessReportSource,
  deactivateBusinessReportSource,
  getBusinessReportIntegrationOptions,
  getBusinessReportSourceGovernanceOptions,
  getBusinessReportSources,
  testBusinessReportSource,
  updateBusinessReportSource,
  manualRefreshBusinessReportSource,
  previewBusinessReportSchedule,
  getBusinessReportNotificationRoutes,
  replaceBusinessReportNotificationRoutes,
  decideBusinessRevenueValidationCase,
  getBusinessReportNotificationInbox,
  acknowledgeBusinessReportNotification,
  createBusinessHistoricalBackfill,
  getBusinessHistoricalBackfills,
  pauseBusinessHistoricalBackfill,
  resumeBusinessHistoricalBackfill,
  type BusinessReportIntegrationOption,
  type BusinessReportSource,
  type BusinessReportSourceGovernanceOptions,
  type BusinessReportSourceInput,
  type BusinessReportSourcePreview,
  type BusinessReportSourceRefreshMode,
  type BusinessReportSourceType,
  type BusinessReportSchedulePreview,
  type BusinessReportNotificationRoute,
  type BusinessRevenueValidationCase,
  type BusinessReportInAppNotification,
  type BusinessHistoricalBackfill,
} from '../lib/api/business-reports';
import { AdminAlertModal, AdminConfirmModal, Loader } from './common';

const SOURCE_TYPES: BusinessReportSourceType[] = ['LOOKER', 'API', 'CSV_IMPORT', 'EXCEL_IMPORT', 'MANUAL', 'LOCAL_DATABASE'];
const REFRESH_MODES: BusinessReportSourceRefreshMode[] = ['MANUAL', 'SCHEDULED', 'FILE_UPLOAD', 'ON_DEMAND'];
const HISTORICAL_BACKFILL_SOURCES = [
  { key: 'LOOKER_HOSPITAL_REVENUE_794', label: 'Revenue 794' },
  { key: 'LOOKER_ESCROW_REFUND_784', label: 'Refund 784' },
  { key: 'LOOKER_NEW_PATIENT_REGISTRATION_401', label: 'Registrations 401' },
  { key: 'LOOKER_TREATMENT_ADVICE_1497', label: 'IVF Advice 1497' },
  { key: 'LOOKER_BILLED_OPU_794', label: 'Fresh OPU · Revenue 794' },
] as const;
const TERMINAL_HISTORICAL_BACKFILL_STATUSES = new Set<BusinessHistoricalBackfill['status']>([
  'COMPLETED',
  'COMPLETED_WITH_ERRORS',
  'CANCELLED',
]);

type SourceForm = {
  sourceKey: string;
  sourceName: string;
  description: string;
  sourceType: BusinessReportSourceType;
  provider: string;
  adapterKey: string;
  integrationSettingKey: string;
  sourceConfiguration: string;
  fieldMapping: string;
  centerMapping: string;
  refreshMode: BusinessReportSourceRefreshMode;
  refreshCron: string;
  refreshTimezone: string;
  refreshConfiguration: string;
  ownerName: string;
  ownerContact: string;
  scheduleUnit: 'MINUTE' | 'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  scheduleMode: 'RUNS_PER_PERIOD' | 'EVERY_INTERVAL';
  scheduleEnabled: boolean;
  scheduleCount: string;
  scheduleAnchorTime: string;
  lateArrivalDays: string;
  reconciliationDates: string;
  validationSteps: ValidationStepForm[];
  eligibilityFilters: EligibilityFilterForm[];
};

type EligibilityFilterForm = {
  field: string;
  operator: 'EQUALS' | 'IN' | 'EXCLUDE_EXACT' | 'IS_NOT_BLANK' | 'IS_NOT_NULL';
  values: string;
};

type ValidationStepForm = {
  name: string;
  assigneeType: 'ROLE' | 'USER';
  assigneeKey: string;
  condition: 'ALWAYS' | 'NEGATIVE_AMOUNT' | 'ZERO_AMOUNT_EQUAL_CHARGE_UC' | 'NEGATIVE_REFUND_AMOUNT' | 'ZERO_REFUND_AMOUNT' | 'MISSING_SOURCE_REFERENCE' | 'REFUND_LINKAGE_WARNING' | 'AMOUNT_AT_LEAST' | 'PROCEDURE_MATCH';
  threshold: string;
};

const EMPTY_FORM: SourceForm = {
  sourceKey: '',
  sourceName: '',
  description: '',
  sourceType: 'LOOKER',
  provider: 'looker',
  adapterKey: 'LOOKER_V1',
  integrationSettingKey: '',
  sourceConfiguration: '{}',
  fieldMapping: '{}',
  centerMapping: '{}',
  refreshMode: 'MANUAL',
  refreshCron: '',
  refreshTimezone: 'Asia/Kolkata',
  refreshConfiguration: '{}',
  ownerName: '',
  ownerContact: '',
  scheduleUnit: 'DAY',
  scheduleMode: 'RUNS_PER_PERIOD',
  scheduleEnabled: true,
  scheduleCount: '6',
  scheduleAnchorTime: '23:59',
  lateArrivalDays: '7',
  reconciliationDates: '',
  validationSteps: [],
  eligibilityFilters: [],
};

export function BusinessReportSourcesTab({ active, token, onTopBarActionsChange }: { active: boolean; token?: string; onTopBarActionsChange?: (actions: ReactNode) => void }) {
  const [sources, setSources] = useState<BusinessReportSource[]>([]);
  const [integrationOptions, setIntegrationOptions] = useState<BusinessReportIntegrationOption[]>([]);
  const [governanceOptions, setGovernanceOptions] = useState<BusinessReportSourceGovernanceOptions>({ roles: [], users: [] });
  const [search, setSearch] = useState('');
  const [sourceType, setSourceType] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<SourceForm | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [preview, setPreview] = useState<BusinessReportSourcePreview | null>(null);
  const [confirmation, setConfirmation] = useState<{ source: BusinessReportSource; action: 'activate' | 'deactivate' } | null>(null);
  const [notificationSource, setNotificationSource] = useState<BusinessReportSource | null>(null);
  const [notificationRoutes, setNotificationRoutes] = useState<BusinessReportNotificationRoute[]>([]);
  const [notifications, setNotifications] = useState<BusinessReportInAppNotification[]>([]);
  const [backfills, setBackfills] = useState<BusinessHistoricalBackfill[]>([]);
  const [backfillFrom, setBackfillFrom] = useState('2024-01-01');
  const [backfillTo, setBackfillTo] = useState(() => istYesterday());
  const [backfillSourceKeys, setBackfillSourceKeys] = useState<string[]>(HISTORICAL_BACKFILL_SOURCES.map((source) => source.key));
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sourceRows, options, governance, inbox, historicalBackfills] = await Promise.all([
        getBusinessReportSources(token, { search, sourceType, status }),
        getBusinessReportIntegrationOptions(token),
        getBusinessReportSourceGovernanceOptions(token),
        getBusinessReportNotificationInbox(token),
        getBusinessHistoricalBackfills(token),
      ]);
      setSources(sourceRows);
      setIntegrationOptions(options);
      setGovernanceOptions(governance);
      setNotifications(inbox);
      setBackfills(historicalBackfills);
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load data sources', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [search, sourceType, status, token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  useEffect(() => {
    if (!active || !backfills.some((run) => run.status === 'RUNNING')) return;
    const handle = window.setInterval(() => {
      getBusinessHistoricalBackfills(token).then(setBackfills).catch(() => undefined);
    }, 10_000);
    return () => window.clearInterval(handle);
  }, [active, backfills, token]);

  const stats = useMemo(() => ({
    total: sources.length,
    active: sources.filter((source) => source.isActive).length,
    valid: sources.filter((source) => source.validationStatus === 'VALID').length,
    attention: sources.filter((source) => source.validationStatus === 'INVALID').length,
  }), [sources]);

  const activeBackfill = backfills.find((run) => !TERMINAL_HISTORICAL_BACKFILL_STATUSES.has(run.status)) || null;
  const currentBackfill = activeBackfill || backfills[0] || null;

  useEffect(() => {
    if (!activeBackfill) return;
    setBackfillFrom(activeBackfill.dateFrom.slice(0, 10));
    setBackfillTo(activeBackfill.dateTo.slice(0, 10));
    setBackfillSourceKeys([...activeBackfill.sourceKeys]);
  }, [activeBackfill]);

  const openCreate = useCallback(() => {
    setEditingKey(null);
    setForm({ ...EMPTY_FORM });
  }, []);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(<button className="btn btn-primary compact-btn" onClick={openCreate}>+ Add Data Source</button>);
    return () => onTopBarActionsChange(null);
  }, [active, onTopBarActionsChange, openCreate]);

  if (!active) return null;

  const openEdit = (source: BusinessReportSource) => {
    const sourceConfiguration = objectValue(source.sourceConfiguration);
    const refreshConfiguration = objectValue(source.refreshConfiguration);
    const schedulePolicy = objectValue(refreshConfiguration.schedulePolicy);
    const lateArrivalPolicy = objectValue(refreshConfiguration.lateArrivalPolicy);
    const workflow = objectValue(sourceConfiguration.validationWorkflow);
    setEditingKey(source.sourceKey);
    setForm({
      sourceKey: source.sourceKey,
      sourceName: source.sourceName,
      description: source.description || '',
      sourceType: source.sourceType,
      provider: source.provider,
      adapterKey: source.adapterKey,
      integrationSettingKey: source.integrationSettingKey || '',
      sourceConfiguration: prettyJson(source.sourceConfiguration),
      fieldMapping: prettyJson(source.fieldMapping),
      centerMapping: prettyJson(source.centerMapping),
      refreshMode: source.refreshMode,
      refreshCron: source.refreshCron || '',
      refreshTimezone: source.refreshTimezone,
      refreshConfiguration: prettyJson(source.refreshConfiguration),
      ownerName: source.ownerName || '',
      ownerContact: source.ownerContact || '',
      scheduleUnit: scheduleUnit(schedulePolicy.unit),
      scheduleMode: String(schedulePolicy.mode).toUpperCase() === 'EVERY_INTERVAL' ? 'EVERY_INTERVAL' : 'RUNS_PER_PERIOD',
      scheduleEnabled: schedulePolicy.enabled !== false,
      scheduleCount: String(schedulePolicy.count || 6),
      scheduleAnchorTime: String(schedulePolicy.anchorTime || '23:59'),
      lateArrivalDays: String(lateArrivalPolicy.previousMonthGraceDays ?? 7),
      reconciliationDates: (Array.isArray(sourceConfiguration.reconciliationDates) ? sourceConfiguration.reconciliationDates : []).join('\n'),
      validationSteps: validationStepForms(workflow.steps),
      eligibilityFilters: eligibilityFilterForms(sourceConfiguration.fixedFilters),
    });
  };

  const manualRefresh = async (source: BusinessReportSource) => {
    setLoading(true);
    try {
      const result = await manualRefreshBusinessReportSource(source.sourceKey, token);
      const readOnly = String(result.executionMode || '') === 'LIVE_SAMPLE';
      setAlert({ type: 'success', title: readOnly ? 'Read-only preview complete' : 'Manual refresh complete', message: readOnly ? 'The inactive source was queried safely and no facts were written.' : 'The governed range refresh completed. Review its batch and validation cases.' });
      await load();
    } catch (error) { setAlert({ type: 'error', title: 'Manual refresh failed', message: errorMessage(error) }); }
    finally { setLoading(false); }
  };

  const startBackfill = async () => {
    if (!backfillSourceKeys.length) {
      setAlert({ type: 'error', title: 'Select a source', message: 'Select at least one governed source for the historical backfill.' });
      return;
    }
    setLoading(true);
    try {
      const created = await createBusinessHistoricalBackfill({
        from: backfillFrom,
        to: backfillTo,
        sourceKeys: backfillSourceKeys,
      }, token);
      setBackfills([created, ...backfills.filter((run) => run.id !== created.id)]);
      setAlert({ type: 'success', title: 'Historical backfill started', message: 'Selected governed sources will run sequentially in monthly windows. Oversized windows split automatically.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not start historical backfill', message: errorMessage(error) });
    } finally { setLoading(false); }
  };

  const changeBackfillState = async (action: 'pause' | 'resume') => {
    if (!activeBackfill) return;
    setLoading(true);
    try {
      const changed = action === 'pause'
        ? await pauseBusinessHistoricalBackfill(activeBackfill.id, token)
        : await resumeBusinessHistoricalBackfill(activeBackfill.id, token);
      setBackfills([changed, ...backfills.filter((run) => run.id !== changed.id)]);
    } catch (error) {
      setAlert({ type: 'error', title: `Could not ${action} historical backfill`, message: errorMessage(error) });
    } finally { setLoading(false); }
  };

  const openNotifications = async (source: BusinessReportSource) => {
    try { setNotificationRoutes(await getBusinessReportNotificationRoutes(source.sourceKey, token)); setNotificationSource(source); }
    catch (error) { setAlert({ type: 'error', title: 'Could not load notification routing', message: errorMessage(error) }); }
  };

  const save = async () => {
    if (!form) return;
    try {
      const input = formInput(form);
      setLoading(true);
      if (editingKey) await updateBusinessReportSource(editingKey, input, token);
      else await createBusinessReportSource(input, token);
      setForm(null);
      setEditingKey(null);
      await load();
      setAlert({
        type: 'success',
        title: editingKey ? 'Source updated' : 'Source created',
        message: 'The source is inactive and must pass Test & Preview before activation.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save source', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const runPreview = async (source: BusinessReportSource) => {
    setLoading(true);
    try {
      const result = await testBusinessReportSource(source.sourceKey, token);
      setPreview(result);
      await load();
    } catch (error) {
      setAlert({ type: 'error', title: 'Source test failed', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const confirmStatusChange = async () => {
    if (!confirmation) return;
    const { source, action } = confirmation;
    setConfirmation(null);
    setLoading(true);
    try {
      if (action === 'activate') await activateBusinessReportSource(source.sourceKey, token);
      else await deactivateBusinessReportSource(source.sourceKey, token);
      await load();
      setAlert({
        type: 'success',
        title: action === 'activate' ? 'Source activated' : 'Source deactivated',
        message: `${source.sourceName} is now ${action === 'activate' ? 'available to approved adapters' : 'inactive'}.`,
      });
    } catch (error) {
      setAlert({ type: 'error', title: `Could not ${action} source`, message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="tab-content business-source-page" id="tab-business-report-sources">
      {loading && <Loader message="Processing Business Report source configuration..." />}

      <div className="business-source-kpis">
        <SourceKpi label="Configured sources" value={stats.total} tone="neutral" />
        <SourceKpi label="Active" value={stats.active} tone="success" />
        <SourceKpi label="Validated" value={stats.valid} tone="info" />
        <SourceKpi label="Needs attention" value={stats.attention} tone="danger" />
      </div>

      <div className="enterprise-card business-source-table-card business-backfill-card">
        <div className="enterprise-card-heading business-backfill-heading">
          <div>
            <strong>Historical Looker Backfill</strong>
            <span>Loads the selected governed Looker sources sequentially into the persisted ledger. Monthly windows automatically split when provider limits require it.</span>
          </div>
          {currentBackfill && <SourceStatus value={currentBackfill.status} />}
        </div>
        <div className="business-backfill-controls">
          <div className="input-group">
            <label htmlFor="historical-backfill-from">From</label>
            <input id="historical-backfill-from" type="date" min="2024-01-01" value={backfillFrom} disabled={Boolean(activeBackfill)} onChange={(event) => setBackfillFrom(event.target.value)} />
          </div>
          <div className="input-group">
            <label htmlFor="historical-backfill-to">Through</label>
            <input id="historical-backfill-to" type="date" max={istYesterday()} value={backfillTo} disabled={Boolean(activeBackfill)} onChange={(event) => setBackfillTo(event.target.value)} />
          </div>
          <fieldset className="business-backfill-sources" disabled={Boolean(activeBackfill)}>
            <legend>Sources</legend>
            <div className="business-backfill-source-options">
              {HISTORICAL_BACKFILL_SOURCES.map((source) => (
                <label key={source.key} className={backfillSourceKeys.includes(source.key) ? 'selected' : ''}>
                  <input
                    type="checkbox"
                    checked={backfillSourceKeys.includes(source.key)}
                    onChange={(event) => setBackfillSourceKeys(event.target.checked
                      ? [...backfillSourceKeys, source.key]
                      : backfillSourceKeys.filter((sourceKey) => sourceKey !== source.key))}
                  />
                  <span>{source.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="business-backfill-action">
            {!activeBackfill ? (
              <button className="btn business-backfill-button business-backfill-button--start" onClick={() => void startBackfill()}>Start Backfill</button>
            ) : activeBackfill.status === 'RUNNING' ? (
              <button className="btn business-backfill-button business-backfill-button--pause" onClick={() => void changeBackfillState('pause')}>Pause Backfill</button>
            ) : (
              <button className="btn business-backfill-button business-backfill-button--resume" onClick={() => void changeBackfillState('resume')}>Resume / Retry</button>
            )}
            <span>{activeBackfill ? 'Configuration locked to active job' : `${backfillSourceKeys.length} of ${HISTORICAL_BACKFILL_SOURCES.length} sources selected`}</span>
          </div>
        </div>
        {currentBackfill ? (
          <div className="enterprise-table-wrap">
            <table className="enterprise-table">
              <thead><tr><th>Progress</th><th>Window</th><th>Status</th><th>Rows</th><th>Imported / Existing</th><th>Result</th></tr></thead>
              <tbody>
                <tr>
                  <td><strong>{currentBackfill.completedWindows} / {currentBackfill.totalWindows}</strong><span className="business-source-secondary">{backfillProgress(currentBackfill)}%</span></td>
                  <td>{currentBackfill.dateFrom.slice(0, 10)} through {currentBackfill.dateTo.slice(0, 10)}<span className="business-source-secondary">Started by {currentBackfill.createdBy}</span></td>
                  <td><SourceStatus value={currentBackfill.status} /></td>
                  <td>{currentBackfill.windows.reduce((sum, window) => sum + window.totalRows, 0).toLocaleString()}</td>
                  <td>{currentBackfill.windows.reduce((sum, window) => sum + window.importedRows, 0).toLocaleString()} / {currentBackfill.windows.reduce((sum, window) => sum + window.duplicateRows, 0).toLocaleString()}</td>
                  <td>{currentBackfill.lastErrorCode || 'No blocking error'}<span className="business-source-secondary">{currentBackfill.sanitizedLastError || 'Each completed window is committed before the next begins.'}</span></td>
                </tr>
                {currentBackfill.windows.filter((window) => window.status !== 'SPLIT').slice(-12).map((window) => (
                  <tr key={window.id}>
                    <td>{historicalBackfillSourceLabel(window.sourceKey)}</td>
                    <td>{window.dateFrom.slice(0, 10)} – {window.dateTo.slice(0, 10)}<span className="business-source-secondary">Attempt {window.attemptCount}</span></td>
                    <td><SourceStatus value={window.status} /></td>
                    <td>{window.totalRows.toLocaleString()}</td>
                    <td>{window.importedRows.toLocaleString()} / {window.duplicateRows.toLocaleString()}</td>
                    <td>{window.importBatchId ? `Batch ${window.importBatchId.slice(0, 8)}` : window.lastErrorCode || 'Waiting'}<span className="business-source-secondary">{window.sanitizedLastError || ''}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="business-source-empty"><strong>No historical backfill created</strong><span>The default range starts on 1 April 2025 and ends yesterday IST.</span></div>}
      </div>

      <div className="business-source-filter enterprise-card">
        <div className="input-group">
          <label htmlFor="business-source-search">Search</label>
          <input id="business-source-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, key, provider, adapter or owner" />
        </div>
        <div className="input-group">
          <label htmlFor="business-source-type">Source Type</label>
          <select id="business-source-type" value={sourceType} onChange={(event) => setSourceType(event.target.value)}>
            <option value="ALL">All source types</option>
            {SOURCE_TYPES.map((type) => <option key={type} value={type}>{label(type)}</option>)}
          </select>
        </div>
        <div className="input-group">
          <label htmlFor="business-source-status">Status</label>
          <select id="business-source-status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="PENDING">Validation pending</option>
            <option value="VALID">Validated</option>
            <option value="INVALID">Invalid</option>
          </select>
        </div>
        <button className="btn btn-outline" onClick={() => void load()}>Refresh</button>
      </div>

      <div className="enterprise-card business-source-table-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Governed Source Registry</strong>
            <span>Only validated and explicitly confirmed sources can be activated.</span>
          </div>
          <span className="business-source-count">{sources.length} shown</span>
        </div>
        {sources.length ? (
          <div className="enterprise-table-wrap">
            <table className="enterprise-table business-source-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Type / Provider</th>
                  <th>Status</th>
                  <th>Owner</th>
                  <th>Refresh Health</th>
                  <th>Sanitized Error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.id}>
                    <td>
                      <strong>{source.sourceName}</strong>
                      <span className="business-source-secondary">{source.sourceKey}</span>
                      <span className="business-source-secondary">{source.adapterKey}</span>
                    </td>
                    <td>
                      <span className="source-type-chip">{label(source.sourceType)}</span>
                      <span className="business-source-secondary">{source.provider}</span>
                    </td>
                    <td>
                      <div className="source-status-stack">
                        <SourceStatus value={source.isActive ? 'ACTIVE' : 'INACTIVE'} />
                        <SourceStatus value={source.validationStatus} />
                        {!source.isActive && <span className="business-source-secondary">{source.statusReason || 'Inactive pending administrator activation.'}</span>}
                      </div>
                    </td>
                    <td>
                      <strong>{source.ownerName || 'Not assigned'}</strong>
                      <span className="business-source-secondary">{source.ownerContact || '—'}</span>
                    </td>
                    <td>
                      <span>Last success: {dateTime(source.lastSuccessAt)}</span>
                      <span className="business-source-secondary">Last test: {dateTime(source.lastAttemptAt)}</span>
                      <span className="business-source-secondary">{label(source.refreshMode)}</span>
                    </td>
                    <td className="business-source-error">{source.sanitizedLastError || 'No error'}</td>
                    <td>
                      <div className="business-source-actions" aria-label={`Actions for ${source.sourceName}`}>
                        <button className="btn btn-sm business-source-action business-source-action-edit" onClick={() => openEdit(source)}>Edit</button>
                        <button className="btn btn-sm business-source-action business-source-action-test" onClick={() => void runPreview(source)}>Test & Preview</button>
                        {source.sourceType === 'LOOKER' && <button className="btn btn-sm business-source-action business-source-action-refresh" onClick={() => void manualRefresh(source)}>Manual Refresh</button>}
                        <button className="btn btn-sm business-source-action business-source-action-notify" onClick={() => void openNotifications(source)}>Notifications</button>
                        <button
                          className={`btn btn-sm business-source-action ${source.isActive ? 'business-source-action-deactivate' : 'business-source-action-activate'}`}
                          disabled={!source.isActive && source.validationStatus !== 'VALID'}
                          title={!source.isActive && source.validationStatus !== 'VALID' ? 'Run Test & Preview successfully first' : undefined}
                          onClick={() => setConfirmation({ source, action: source.isActive ? 'deactivate' : 'activate' })}
                        >
                          {source.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="business-source-empty">
            <div className="business-source-empty-icon">⇄</div>
            <strong>No governed data sources found</strong>
            <span>Add a source when its business definition, adapter and center mappings are ready.</span>
            <button className="btn btn-primary" onClick={openCreate}>Add Data Source</button>
          </div>
        )}
      </div>

      <NotificationInbox notifications={notifications} token={token} onChanged={load} />

      {form && (
        <SourceFormModal
          value={form}
          editing={Boolean(editingKey)}
          integrationOptions={integrationOptions}
          governanceOptions={governanceOptions}
          token={token}
          onChange={setForm}
          onSave={() => void save()}
          onClose={() => { setForm(null); setEditingKey(null); }}
        />
      )}

      {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}

      {notificationSource && <NotificationRoutesModal
        source={notificationSource}
        routes={notificationRoutes}
        governanceOptions={governanceOptions}
        onClose={() => setNotificationSource(null)}
        onSave={async (routes) => {
          try { await replaceBusinessReportNotificationRoutes(notificationSource.sourceKey, routes.map(({ id: _id, ...route }) => route), token); setNotificationSource(null); setAlert({ type: 'success', title: 'Notification routing saved', message: 'Role/user delivery, channels and escalation rules are now effective dynamically.' }); }
          catch (error) { setAlert({ type: 'error', title: 'Could not save notification routing', message: errorMessage(error) }); }
        }}
      />}

      {confirmation && (
        <AdminConfirmModal
          title={confirmation.action === 'activate' ? 'Activate Data Source?' : 'Deactivate Data Source?'}
          message={confirmation.action === 'activate'
            ? `Activate ${confirmation.source.sourceName}? Only approved adapters will be allowed to resolve it.`
            : `Deactivate ${confirmation.source.sourceName}? Scheduled adapters must stop using it immediately.`}
          onConfirm={() => void confirmStatusChange()}
          onCancel={() => setConfirmation(null)}
        />
      )}

      {alert && (
        <AdminAlertModal
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </section>
  );
}

function SourceFormModal({
  value,
  editing,
  integrationOptions,
  governanceOptions,
  token,
  onChange,
  onSave,
  onClose,
}: {
  value: SourceForm;
  editing: boolean;
  integrationOptions: BusinessReportIntegrationOption[];
  governanceOptions: BusinessReportSourceGovernanceOptions;
  token?: string;
  onChange: (value: SourceForm) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const set = <K extends keyof SourceForm>(key: K, next: SourceForm[K]) => onChange({ ...value, [key]: next });
  const updateStep = (index: number, patch: Partial<ValidationStepForm>) => set('validationSteps', value.validationSteps.map((step, stepIndex) => stepIndex === index ? { ...step, ...patch } : step));
  const updateEligibilityFilter = (index: number, patch: Partial<EligibilityFilterForm>) => set('eligibilityFilters', value.eligibilityFilters.map((filter, filterIndex) => filterIndex === index ? { ...filter, ...patch } : filter));
  const [schedulePreview, setSchedulePreview] = useState<BusinessReportSchedulePreview | null>(null);
  const [scheduleError, setScheduleError] = useState('');
  useEffect(() => {
    if (value.refreshMode !== 'SCHEDULED') { setSchedulePreview(null); setScheduleError(''); return; }
    const handle = window.setTimeout(() => {
      const configuration = { ...objectValue(parseJsonObjectSafe(value.refreshConfiguration)), schedulePolicy: { mode: value.scheduleMode, unit: value.scheduleUnit, count: Number(value.scheduleCount), anchorTime: value.scheduleAnchorTime, enabled: value.scheduleEnabled } };
      previewBusinessReportSchedule(configuration, token).then((result) => { setSchedulePreview(result); setScheduleError(''); }).catch((error) => { setSchedulePreview(null); setScheduleError(errorMessage(error)); });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [token, value.refreshConfiguration, value.refreshMode, value.scheduleAnchorTime, value.scheduleCount, value.scheduleEnabled, value.scheduleMode, value.scheduleUnit]);
  return (
    <div className="business-source-modal-overlay">
      <div className="business-source-modal">
        <div className="business-source-modal-header">
          <div>
            <p className="eyebrow">Governed Source Registry</p>
            <h3>{editing ? 'Edit Data Source' : 'Add Data Source'}</h3>
            <span>Credential values are prohibited. Select an Integration Settings reference instead.</span>
          </div>
          <button className="admin-modal-close" onClick={onClose} aria-label="Close source form">&times;</button>
        </div>
        <div className="business-source-form-grid">
          <Field label="Source Key" help="Immutable uppercase identifier, for example MEDICS_DAILY_REVENUE.">
            <input value={value.sourceKey} disabled={editing} onChange={(event) => set('sourceKey', event.target.value.toUpperCase())} />
          </Field>
          <Field label="Source Name">
            <input value={value.sourceName} onChange={(event) => set('sourceName', event.target.value)} />
          </Field>
          <Field label="Source Type">
            <select value={value.sourceType} onChange={(event) => set('sourceType', event.target.value as BusinessReportSourceType)}>
              {SOURCE_TYPES.map((type) => <option key={type} value={type}>{label(type)}</option>)}
            </select>
          </Field>
          <Field label="Provider" help="Provider identity only; no URL or credential.">
            <input value={value.provider} onChange={(event) => set('provider', event.target.value)} />
          </Field>
          <Field label="Adapter Key" help="Approved backend adapter boundary.">
            <input value={value.adapterKey} onChange={(event) => set('adapterKey', event.target.value.toUpperCase())} />
          </Field>
          <Field label="Integration Settings Reference" help="Only key/name/provider metadata is shown. Secret values are never returned.">
            <select value={value.integrationSettingKey} onChange={(event) => set('integrationSettingKey', event.target.value)}>
              <option value="">No Integration Settings reference</option>
              {integrationOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.name || option.key} — {option.provider} ({option.setting_kind})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Refresh Mode">
            <select value={value.refreshMode} onChange={(event) => set('refreshMode', event.target.value as BusinessReportSourceRefreshMode)}>
              {REFRESH_MODES.map((mode) => <option key={mode} value={mode}>{label(mode)}</option>)}
            </select>
          </Field>
          <Field label="Refresh Schedule" help="Required only for scheduled sources; use an approved cron expression.">
            <input value={value.refreshMode === 'SCHEDULED' ? scheduleSummary(value) : 'Not scheduled'} disabled />
          </Field>
          {value.refreshMode === 'SCHEDULED' && <>
            <Field label="Schedule type">
              <select value={value.scheduleMode} onChange={(event) => set('scheduleMode', event.target.value as SourceForm['scheduleMode'])}><option value="RUNS_PER_PERIOD">Runs per period</option><option value="EVERY_INTERVAL">Every interval</option></select>
            </Field>
            <Field label="Schedule enabled"><input type="checkbox" checked={value.scheduleEnabled} onChange={(event) => set('scheduleEnabled', event.target.checked)} /></Field>
            <Field label="Schedule period" help="The count means runs within the selected period; Hour + 2 means twice each hour.">
              <select value={value.scheduleUnit} onChange={(event) => set('scheduleUnit', event.target.value as SourceForm['scheduleUnit'])}>
                {['MINUTE', 'HOUR', 'DAY', 'WEEK', 'MONTH', 'YEAR'].map((unit) => <option key={unit} value={unit}>{label(unit)}</option>)}
              </select>
            </Field>
            <Field label={value.scheduleMode === 'RUNS_PER_PERIOD' ? 'Runs per period' : 'Every N units'} help={value.scheduleMode === 'EVERY_INTERVAL' && value.scheduleUnit === 'MINUTE' ? 'Minimum interval: 15 minutes.' : undefined}>
              <input type="number" min={value.scheduleMode === 'EVERY_INTERVAL' && value.scheduleUnit === 'MINUTE' ? 15 : 1} value={value.scheduleCount} onChange={(event) => set('scheduleCount', event.target.value)} />
            </Field>
            <Field label="Last run / anchor time (IST)">
              <input type="time" value={value.scheduleAnchorTime} onChange={(event) => set('scheduleAnchorTime', event.target.value)} />
            </Field>
            <Field label="Previous-month late-arrival window" help="During these first days of a new month, the previous month is rechecked. Later changes become reconciliation exceptions.">
              <input type="number" min={0} max={31} value={value.lateArrivalDays} onChange={(event) => set('lateArrivalDays', event.target.value)} />
            </Field>
            <div className="business-source-field full"><span>Calculated schedule</span>{scheduleError ? <small className="business-source-error">{scheduleError}</small> : schedulePreview ? <><strong>{schedulePreview.description}</strong><small>Next: {schedulePreview.nextExecution ? dateTime(schedulePreview.nextExecution) : 'Disabled'} · Estimated Looker requests: {schedulePreview.estimatedRequestsPerDay}/day, {schedulePreview.estimatedRequestsPerMonth}/month</small><ol>{schedulePreview.nextExecutions.map((date) => <li key={date}>{dateTime(date)}</li>)}</ol>{schedulePreview.warning && <small className="business-source-error">{schedulePreview.warning}</small>}</> : <small>Enter a valid schedule to see its next executions.</small>}</div>
          </>}
          <Field label="Timezone">
            <input value={value.refreshTimezone} onChange={(event) => set('refreshTimezone', event.target.value)} />
          </Field>
          <Field label="Owner">
            <input value={value.ownerName} onChange={(event) => set('ownerName', event.target.value)} />
          </Field>
          <Field label="Owner Contact">
            <input value={value.ownerContact} onChange={(event) => set('ownerContact', event.target.value)} />
          </Field>
          <Field label="Description" full>
            <textarea rows={2} value={value.description} onChange={(event) => set('description', event.target.value)} />
          </Field>
          <Field label="Reconciliation dates" help="One YYYY-MM-DD date per line. These can be replaced before activation." full>
            <textarea rows={3} value={value.reconciliationDates} onChange={(event) => set('reconciliationDates', event.target.value)} placeholder={'2024-10-09\n2025-07-31'} />
          </Field>
          <div className="business-source-field full">
            <span>Governed eligibility filters</span>
            <small>These source rules are applied on every run. Date and centre remain report-time filters. Saving any change makes the source inactive and requires Test &amp; Preview plus approval before reactivation.</small>
            {value.eligibilityFilters.map((filter, index) => (
              <div className="business-source-form-grid" key={`${index}-${filter.field}`}>
                <Field label={`Filter ${index + 1} source field`} help="Exact Looker field, for example patients.active_flag.">
                  <input value={filter.field} onChange={(event) => updateEligibilityFilter(index, { field: event.target.value })} placeholder="view.field_name" />
                </Field>
                <Field label="Controlled operator">
                  <select value={filter.operator} onChange={(event) => updateEligibilityFilter(index, { operator: event.target.value as EligibilityFilterForm['operator'] })}>
                    <option value="EQUALS">Equals / approved Looker expression</option>
                    <option value="IN">Include listed values</option>
                    <option value="EXCLUDE_EXACT">Exclude listed exact values</option>
                    <option value="IS_NOT_BLANK">Is not blank</option>
                    <option value="IS_NOT_NULL">Is not null (date/database field)</option>
                  </select>
                </Field>
                {!['IS_NOT_BLANK', 'IS_NOT_NULL'].includes(filter.operator) && <Field label={['IN', 'EXCLUDE_EXACT'].includes(filter.operator) ? 'Values (one per line)' : 'Value / saved Looker expression'} full>
                  <textarea rows={2} value={filter.values} onChange={(event) => updateEligibilityFilter(index, { values: event.target.value })} />
                </Field>}
                <button className="btn btn-outline btn-sm" type="button" onClick={() => set('eligibilityFilters', value.eligibilityFilters.filter((_, filterIndex) => filterIndex !== index))}>Remove filter</button>
              </div>
            ))}
            <button className="btn btn-outline btn-sm" type="button" onClick={() => set('eligibilityFilters', [...value.eligibilityFilters, { field: '', operator: 'EQUALS', values: '' }])}>+ Add eligibility filter</button>
          </div>
          <div className="business-source-field full">
            <span>Ordered validation workflow</span>
            <small>Select a Role or User for every tier. Conditional tiers can require Finance or another checker only for specific exceptions.</small>
            {value.validationSteps.map((step, index) => (
              <div className="business-source-form-grid" key={`${index}-${step.name}`}>
                <Field label={`Tier ${index + 1} name`}><input value={step.name} onChange={(event) => updateStep(index, { name: event.target.value })} /></Field>
                <Field label="Assign to">
                  <select value={step.assigneeType} onChange={(event) => updateStep(index, { assigneeType: event.target.value as 'ROLE' | 'USER', assigneeKey: '' })}>
                    <option value="ROLE">Role</option><option value="USER">User</option>
                  </select>
                </Field>
                <Field label={step.assigneeType === 'ROLE' ? 'Responsible role' : 'Responsible user'}>
                  <select value={step.assigneeKey} onChange={(event) => updateStep(index, { assigneeKey: event.target.value })}>
                    <option value="">Select...</option>
                    {(step.assigneeType === 'ROLE' ? governanceOptions.roles : governanceOptions.users).map((option) => (
                      <option key={option.key} value={option.key}>{'username' in option ? `${option.name} (${option.username})` : option.name}</option>
                    ))}
                  </select>
                </Field>
                <Field label="When required">
                  <select value={step.condition} onChange={(event) => updateStep(index, { condition: event.target.value as ValidationStepForm['condition'] })}>
                    <option value="ALWAYS">Always</option>
                    <option value="NEGATIVE_AMOUNT">Negative amount</option>
                    <option value="ZERO_AMOUNT_EQUAL_CHARGE_UC">Zero amount when Charge = U&amp;C</option>
                    <option value="NEGATIVE_REFUND_AMOUNT">Source-negative refund</option>
                    <option value="ZERO_REFUND_AMOUNT">Zero refund</option>
                    <option value="MISSING_SOURCE_REFERENCE">Missing invoice/reference</option>
                    <option value="REFUND_LINKAGE_WARNING">Refund/revenue linkage warning</option>
                    <option value="AMOUNT_AT_LEAST">Amount at or above threshold</option>
                    <option value="PROCEDURE_MATCH">Configured procedure/type</option>
                  </select>
                </Field>
                {step.condition === 'AMOUNT_AT_LEAST' && <Field label="Threshold (INR)"><input type="number" min={0} value={step.threshold} onChange={(event) => updateStep(index, { threshold: event.target.value })} /></Field>}
                <button className="btn btn-outline btn-sm" type="button" onClick={() => set('validationSteps', value.validationSteps.filter((_, stepIndex) => stepIndex !== index))}>Remove tier</button>
              </div>
            ))}
            <button className="btn btn-outline btn-sm" type="button" onClick={() => set('validationSteps', [...value.validationSteps, { name: `Validation tier ${value.validationSteps.length + 1}`, assigneeType: 'ROLE', assigneeKey: '', condition: value.validationSteps.length ? 'NEGATIVE_AMOUNT' : 'ALWAYS', threshold: '' }])}>+ Add validation tier</button>
          </div>
          <JsonField label="Field Mapping JSON" help='Required. Example: {"revenue":"orders.net_revenue"}' value={value.fieldMapping} onChange={(next) => set('fieldMapping', next)} />
          <JsonField label="Center Mapping JSON" help='Required. Values must be approved canonical codes, for example {"KPHB":"KPHB"}.' value={value.centerMapping} onChange={(next) => set('centerMapping', next)} />
          <JsonField label="Source Configuration JSON" help="Non-secret adapter options only." value={value.sourceConfiguration} onChange={(next) => set('sourceConfiguration', next)} />
          <JsonField label="Refresh Configuration JSON" help="Non-secret scheduling options only." value={value.refreshConfiguration} onChange={(next) => set('refreshConfiguration', next)} />
        </div>
        <div className="business-source-modal-footer">
          <span>Saving always returns the source to inactive / validation pending.</span>
          <div>
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={onSave}>Save Source</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ preview, onClose }: { preview: BusinessReportSourcePreview; onClose: () => void }) {
  return (
    <div className="business-source-modal-overlay">
      <div className="business-source-preview-modal">
        <div className="business-source-modal-header">
          <div>
            <p className="eyebrow">Test & Preview</p>
            <h3>{preview.sourceKey}</h3>
            <span>{preview.message}</span>
          </div>
          <button className="admin-modal-close" onClick={onClose} aria-label="Close preview">&times;</button>
        </div>
        <div className="business-source-preview-summary">
          <SourceStatus value={preview.validationStatus} />
          <span>Rows persisted: 0</span>
          <span>Mode: Configuration only</span>
          <span>Checked: {dateTime(preview.checkedAt)}</span>
        </div>
        {preview.errors.length > 0 && (
          <div className="business-source-preview-errors">
            <strong>Validation errors</strong>
            <ul>{preview.errors.map((error) => <li key={error}>{error}</li>)}</ul>
          </div>
        )}
        <div className="enterprise-table-wrap">
          <table className="enterprise-table">
            <thead><tr><th>Normalized Field</th><th>Configured Source Field</th></tr></thead>
            <tbody>
              {preview.sampleNormalizedFields.length
                ? preview.sampleNormalizedFields.map((field) => <tr key={field.targetField}><td>{field.targetField}</td><td>{field.sourceField}</td></tr>)
                : <tr><td colSpan={2}>No field mappings configured.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="business-source-modal-footer">
          <span>No source rows or report facts were persisted.</span>
          <button className="btn btn-primary" onClick={onClose}>Close Preview</button>
        </div>
      </div>
    </div>
  );
}

function ValidationQueue({ cases, token, onChanged, onError }: { cases: BusinessRevenueValidationCase[]; token?: string; onChanged: () => Promise<void>; onError: (message: string) => void }) {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const decide = async (item: BusinessRevenueValidationCase, action: string) => {
    const reason = (reasons[item.id] || '').trim();
    if (!reason) { onError('Enter a reason or acknowledgment note before recording the decision.'); return; }
    try { await decideBusinessRevenueValidationCase(item.id, action, reason, token); await onChanged(); }
    catch (error) { onError(errorMessage(error)); }
  };
  return <div className="enterprise-card business-source-table-card"><div className="enterprise-card-heading"><div><strong>Revenue validation queue</strong><span>Patient-free maker/checker cases. Pending negatives are excluded; informational zeros remain included as zero.</span></div><span className="business-source-count">{cases.length} open</span></div>
    <div className="enterprise-table-wrap"><table className="enterprise-table"><thead><tr><th>Case / Source</th><th>Centre / Date</th><th>Reference / Procedure</th><th>Signed amount</th><th>Current assignment</th><th>Decision</th></tr></thead><tbody>
      {cases.length ? cases.map((item) => { const activeStep = item.steps.find((step) => ['ACTIVE', 'NEEDS_INFORMATION'].includes(step.status)); return <tr key={item.id}><td><strong>{item.caseReference}</strong><span className="business-source-secondary">{item.exceptionCode} · {item.dataSource.sourceName}</span></td><td>{item.center.displayName}<span className="business-source-secondary">{item.businessDate.slice(0, 10)}</span></td><td>{item.sourceRecordReference}<span className="business-source-secondary">{item.procedureName || 'No procedure supplied'}</span></td><td>{item.currency} {item.signedAmount}</td><td>{activeStep ? `${activeStep.stepName} · ${activeStep.assigneeType} ${activeStep.assigneeKey}` : 'No active tier'}<span className="business-source-secondary">{item.status}</span></td><td><input aria-label={`Decision reason for ${item.caseReference}`} placeholder="Required reason / note" value={reasons[item.id] || ''} onChange={(event) => setReasons({ ...reasons, [item.id]: event.target.value })} /> <div className="business-source-actions">{item.severity === 'INFORMATIONAL' ? <button className="btn btn-outline btn-sm" onClick={() => void decide(item, 'ACKNOWLEDGED')}>Acknowledge</button> : <><button className="btn btn-primary btn-sm" onClick={() => void decide(item, 'APPROVED')}>Approve</button><button className="btn btn-outline btn-sm" onClick={() => void decide(item, 'REJECTED')}>Reject</button><button className="btn btn-outline btn-sm" onClick={() => void decide(item, 'REQUESTED_INFORMATION')}>Request info</button></>}</div></td></tr>; }) : <tr><td colSpan={6}>No open validation cases.</td></tr>}
    </tbody></table></div></div>;
}

function NotificationInbox({ notifications, token, onChanged }: { notifications: BusinessReportInAppNotification[]; token?: string; onChanged: () => Promise<void> }) {
  const groups = groupBusinessReportNotifications(notifications.filter((item) => !item.acknowledgedAt));
  const open = groups.filter((group) => !group.recovered);
  const recovered = groups.filter((group) => group.recovered);
  const acknowledge = async (ids: string[]) => {
    await Promise.all(ids.map((id) => acknowledgeBusinessReportNotification(id, token)));
    await onChanged();
  };
  return <>
    <NotificationGroupTable
      title="Revenue review notifications"
      description="Each unresolved business issue appears once. Retry observations from the same scheduled run are combined."
      countLabel={`${open.length} open ${open.length === 1 ? 'issue' : 'issues'}`}
      groups={open}
      empty="No revenue issues are waiting for review."
      actionLabel="Review issue"
      dismissLabel="Acknowledge issue"
      onDismiss={acknowledge}
    />
    {recovered.length > 0 && <NotificationGroupTable
      title="Recovered run history"
      description="These earlier technical failures were followed by a successful run for the same source and no longer count as open issues."
      countLabel={`${recovered.length} recovered`}
      groups={recovered}
      empty="No recovered run history is awaiting dismissal."
      actionLabel="View recovered run"
      dismissLabel="Dismiss history"
      onDismiss={acknowledge}
    />}
  </>;
}

function NotificationGroupTable({
  title, description, countLabel, groups, empty, actionLabel, dismissLabel, onDismiss,
}: {
  title: string;
  description: string;
  countLabel: string;
  groups: ReturnType<typeof groupBusinessReportNotifications>;
  empty: string;
  actionLabel: string;
  dismissLabel: string;
  onDismiss: (ids: string[]) => Promise<void>;
}) {
  return <div className="enterprise-card business-source-table-card">
    <div className="enterprise-card-heading"><div><strong>{title}</strong><span>{description}</span></div><span className="business-source-count">{countLabel}</span></div>
    <div className="enterprise-table-wrap"><table className="enterprise-table">
      <thead><tr><th>Latest observation</th><th>Source</th><th>Outcome</th><th>Action</th></tr></thead>
      <tbody>{groups.length ? groups.map((group) => <tr key={group.key}>
        <td>{dateTime(group.latest.createdAt)}{group.observationCount > 1 && <span className="business-source-secondary">{group.observationCount} retry observations combined</span>}</td>
        <td>{group.latest.dataSource.sourceName}<span className="business-source-secondary">{group.businessLabel}</span></td>
        <td><strong>{group.recovered ? 'Source recovered' : group.title}</strong><span className="business-source-secondary">{group.recovered && group.recoveredAt ? `A later run completed successfully at ${dateTime(group.recoveredAt)}. ` : ''}{group.message}</span></td>
        <td><div className="business-source-actions"><a className="btn btn-primary btn-sm" href={notificationReviewPath(group.latest)}>{actionLabel}</a><button className="btn btn-outline btn-sm" onClick={() => void onDismiss(group.notificationIds)}>{dismissLabel}</button></div></td>
      </tr>) : <tr><td colSpan={4}>{empty}</td></tr>}</tbody>
    </table></div>
  </div>;
}

export function groupBusinessReportNotifications(notifications: BusinessReportInAppNotification[]) {
  const groups = new Map<string, BusinessReportInAppNotification[]>();
  for (const notification of notifications) {
    const key = notification.issueKey || notification.actionPath
      || `${notification.dataSource.sourceKey}:${notification.triggerCode}:${notification.title}:${notification.message}`;
    groups.set(key, [...(groups.get(key) || []), notification]);
  }
  return [...groups.entries()].map(([key, items]) => {
    const ordered = [...items].sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt));
    const latest = ordered[0];
    const display = notificationDisplay(latest);
    return {
      key,
      latest,
      notificationIds: ordered.map((item) => item.id),
      observationCount: ordered.length,
      recovered: ordered.every((item) => item.lifecycleStatus === 'RECOVERED'),
      recoveredAt: ordered.map((item) => item.recoveredAt).filter(Boolean).sort().at(-1) ?? null,
      ...display,
    };
  }).sort((left, right) => Date.parse(right.latest.createdAt) - Date.parse(left.latest.createdAt));
}

function notificationDisplay(item: BusinessReportInAppNotification) {
  if (item.triggerCode === 'EXISTING_FACT_PAYLOAD_CHANGED') {
    const legacy = item.message.match(/ created BRV-[^ ]+ for (.+) on (\d{4}-\d{2}-\d{2})\.?$/);
    return {
      businessLabel: 'Previously stored transaction values changed',
      title: 'Revenue transaction changed',
      message: legacy
        ? `A transaction from ${item.dataSource.sourceName} changed for ${legacy[1]} on ${legacy[2]}. Open Revenue Validation to compare the current and proposed financial values.`
        : item.message,
    };
  }
  return {
    businessLabel: friendlyTechnicalLabel(item.triggerCode),
    title: item.title,
    message: item.message,
  };
}

function friendlyTechnicalLabel(value: string) {
  const labels: Record<string, string> = {
    NEGATIVE_AMOUNT_REQUIRES_VALIDATION: 'Signed negative amount needs review',
    ZERO_AMOUNT_EQUAL_CHARGE_UC_WARNING: 'Zero-value item needs acknowledgment',
    NEGATIVE_REFUND_AMOUNT_REQUIRES_CORRECTION: 'Negative refund needs correction',
    MISSING_SOURCE_REFERENCE_REQUIRES_CORRECTION: 'Source reference is missing',
    LOOKER_FINANCIAL_FIELD_CONTRACT_BROKEN: 'Source financial format changed',
  };
  return labels[value] || label(value);
}

function notificationReviewPath(item: BusinessReportInAppNotification) {
  if (item.reviewPath?.startsWith('/dashboard/business-reports/')) return item.reviewPath;
  const caseId = item.actionPath?.match(/[?&]case=([0-9a-f-]{36})/i)?.[1];
  if (caseId) return `/dashboard/business-reports/revenue-operations?tab=validation&case=${caseId}`;
  const batchId = item.importBatchId || item.actionPath?.match(/[?&]batch=([0-9a-f-]{36})/i)?.[1];
  return batchId
    ? `/dashboard/business-reports/operations?batch=${batchId}`
    : '/dashboard/business-reports/data-sources';
}

function NotificationRoutesModal({ source, routes, governanceOptions, onClose, onSave }: { source: BusinessReportSource; routes: BusinessReportNotificationRoute[]; governanceOptions: BusinessReportSourceGovernanceOptions; onClose: () => void; onSave: (routes: BusinessReportNotificationRoute[]) => Promise<void> }) {
  const [value, setValue] = useState(routes);
  const update = (index: number, patch: Partial<BusinessReportNotificationRoute>) => setValue(value.map((route, routeIndex) => routeIndex === index ? { ...route, ...patch } : route));
  return <div className="business-source-modal-overlay"><div className="business-source-modal"><div className="business-source-modal-header"><div><p className="eyebrow">Dynamic delivery routing</p><h3>{source.sourceName} notifications</h3><span>No recipient is hardcoded. Role membership and active users are resolved at delivery time.</span></div><button className="admin-modal-close" onClick={onClose}>&times;</button></div><div>
    {value.map((route, index) => <div className="business-source-form-grid" key={route.id}><Field label="Route name"><input value={route.routeName} onChange={(event) => update(index, { routeName: event.target.value })} /></Field><Field label="Trigger"><select value={route.triggerCode} onChange={(event) => update(index, { triggerCode: event.target.value })}><option value="ANY_TECHNICAL_FAILURE">Any technical failure</option><option value="ANY_VALIDATION_CASE">Any validation case</option><option value="LOOKER_FINANCIAL_FIELD_CONTRACT_BROKEN">Malformed financial amount</option><option value="NEGATIVE_AMOUNT_REQUIRES_VALIDATION">Negative amount</option><option value="ZERO_AMOUNT_EQUAL_CHARGE_UC_WARNING">Suspicious zero</option><option value="EXISTING_FACT_PAYLOAD_CHANGED">Changed source payload</option></select></Field><Field label="Recipient type"><select value={route.recipientType} onChange={(event) => update(index, { recipientType: event.target.value as 'ROLE' | 'USER', recipientKey: '' })}><option value="ROLE">Role</option><option value="USER">User</option></select></Field><Field label="Recipient"><select value={route.recipientKey} onChange={(event) => update(index, { recipientKey: event.target.value })}><option value="">Select…</option>{(route.recipientType === 'ROLE' ? governanceOptions.roles : governanceOptions.users).map((option) => <option key={option.key} value={option.key}>{option.name}</option>)}</select></Field><Field label="Tier"><select value={route.recipientTier} onChange={(event) => update(index, { recipientTier: event.target.value as 'PRIMARY' | 'ESCALATION' })}><option value="PRIMARY">Primary</option><option value="ESCALATION">Escalation</option></select></Field><Field label="Channels"><label><input type="checkbox" checked={route.channels.includes('IN_APP')} onChange={(event) => update(index, { channels: event.target.checked ? [...new Set([...route.channels, 'IN_APP' as const])] : route.channels.filter((channel) => channel !== 'IN_APP') })} /> In-app</label><label><input type="checkbox" checked={route.channels.includes('EMAIL')} onChange={(event) => update(index, { channels: event.target.checked ? [...new Set([...route.channels, 'EMAIL' as const])] : route.channels.filter((channel) => channel !== 'EMAIL') })} /> Email</label></Field>{route.recipientTier === 'ESCALATION' && <Field label="Escalation delay (minutes)"><input type="number" min={0} max={43200} value={route.escalationMinutes || 0} onChange={(event) => update(index, { escalationMinutes: Number(event.target.value) })} /></Field>}<Field label="Effective from"><input type="date" value={route.effectiveFrom?.slice(0, 10) || ''} onChange={(event) => update(index, { effectiveFrom: event.target.value || null })} /></Field><Field label="Effective to"><input type="date" value={route.effectiveTo?.slice(0, 10) || ''} onChange={(event) => update(index, { effectiveTo: event.target.value || null })} /></Field><Field label="Enabled"><input type="checkbox" checked={route.isEnabled} onChange={(event) => update(index, { isEnabled: event.target.checked })} /></Field><button className="btn btn-outline btn-sm" onClick={() => setValue(value.filter((_, routeIndex) => routeIndex !== index))}>Remove route</button></div>)}
    <button className="btn btn-outline btn-sm" onClick={() => setValue([...value, { id: `new-${Date.now()}`, routeName: 'Technical notification', triggerCode: 'ANY_TECHNICAL_FAILURE', recipientType: 'ROLE', recipientKey: '', recipientTier: 'PRIMARY', channels: ['IN_APP', 'EMAIL'], escalationMinutes: null, isEnabled: true, effectiveFrom: null, effectiveTo: null }])}>+ Add route</button>
  </div><div className="business-source-modal-footer"><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={() => void onSave(value)}>Save routing</button></div></div></div>;
}

function Field({ label: fieldLabel, help, full, children }: { label: string; help?: string; full?: boolean; children: ReactNode }) {
  return <label className={`business-source-field${full ? ' full' : ''}`}><span>{fieldLabel}</span>{children}{help && <small>{help}</small>}</label>;
}

function JsonField({ label: fieldLabel, help, value, onChange }: { label: string; help: string; value: string; onChange: (value: string) => void }) {
  return <Field label={fieldLabel} help={help} full><textarea className="business-source-json" rows={5} value={value} onChange={(event) => onChange(event.target.value)} spellCheck={false} /></Field>;
}

function SourceKpi({ label: kpiLabel, value, tone }: { label: string; value: number; tone: string }) {
  return <div className={`business-source-kpi ${tone}`}><span>{kpiLabel}</span><strong>{value}</strong></div>;
}

function SourceStatus({ value }: { value: string }) {
  return <span className={`business-source-status ${value.toLowerCase()}`}>{label(value)}</span>;
}

function backfillProgress(run: BusinessHistoricalBackfill): number {
  if (!run.totalWindows) return 0;
  return Math.round((run.completedWindows / run.totalWindows) * 100);
}

function historicalBackfillSourceLabel(sourceKey: string): string {
  return HISTORICAL_BACKFILL_SOURCES.find((source) => source.key === sourceKey)?.label || sourceKey;
}

function istYesterday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(Date.now() - 86_400_000));
}

function formInput(form: SourceForm): BusinessReportSourceInput {
  if (!form.sourceKey.trim() || !form.sourceName.trim() || !form.provider.trim() || !form.adapterKey.trim()) {
    throw new Error('Source key, source name, provider and adapter key are required.');
  }
  const sourceConfiguration = parseJsonObject(form.sourceConfiguration, 'Source Configuration');
  const refreshConfiguration = parseJsonObject(form.refreshConfiguration, 'Refresh Configuration');
  const assignedSteps = form.validationSteps.filter((step) => step.assigneeKey);
  if (form.validationSteps.length && assignedSteps.length !== form.validationSteps.length) throw new Error('Every validation tier must have a responsible role or user.');
  if (assignedSteps.length) {
    sourceConfiguration.validationWorkflow = { steps: assignedSteps.map((step, index) => ({
      step: index + 1,
      name: step.name.trim() || `Validation tier ${index + 1}`,
      assigneeType: step.assigneeType,
      assigneeKey: step.assigneeKey,
      condition: step.condition,
      ...(step.condition === 'AMOUNT_AT_LEAST' ? { threshold: Number(step.threshold) } : {}),
      makerCheckerRequired: true,
    })) };
  } else {
    delete sourceConfiguration.validationWorkflow;
  }
  const fixedFilters: Record<string, unknown> = {};
  for (const filter of form.eligibilityFilters) {
    const field = filter.field.trim();
    if (!field) throw new Error('Every eligibility filter requires an exact source field.');
    if (Object.prototype.hasOwnProperty.call(fixedFilters, field)) throw new Error(`Eligibility filter ${field} is duplicated.`);
    if (filter.operator === 'IS_NOT_BLANK' || filter.operator === 'IS_NOT_NULL') {
      fixedFilters[field] = { operator: filter.operator };
      continue;
    }
    if (filter.operator === 'IN' || filter.operator === 'EXCLUDE_EXACT') {
      const values = filter.values.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
      if (!values.length) throw new Error(`Eligibility filter ${field} requires at least one value.`);
      fixedFilters[field] = { operator: filter.operator, values };
      continue;
    }
    const configuredValue = filter.values.trim();
    if (!configuredValue) throw new Error(`Eligibility filter ${field} requires a value.`);
    fixedFilters[field] = { operator: filter.operator, value: configuredValue };
  }
  if (Object.keys(fixedFilters).length) sourceConfiguration.fixedFilters = fixedFilters;
  else delete sourceConfiguration.fixedFilters;
  sourceConfiguration.reconciliationDates = form.reconciliationDates.split(/[\n,]+/).map((date) => date.trim()).filter(Boolean);
  if (form.refreshMode === 'SCHEDULED') {
    refreshConfiguration.schedulePolicy = { mode: form.scheduleMode, unit: form.scheduleUnit, count: Number(form.scheduleCount), anchorTime: form.scheduleAnchorTime, enabled: form.scheduleEnabled };
    refreshConfiguration.lateArrivalPolicy = { queryWindow: 'CURRENT_MONTH_TO_DATE', previousMonthGraceDays: Number(form.lateArrivalDays), afterCloseAction: 'RAISE_RECONCILIATION_EXCEPTION' };
    refreshConfiguration.manualRefreshEnabled = true;
    refreshConfiguration.businessDateOffsetDays = Number(refreshConfiguration.businessDateOffsetDays ?? 0);
  }
  return {
    sourceKey: form.sourceKey.trim().toUpperCase(),
    sourceName: form.sourceName.trim(),
    description: form.description.trim() || null,
    sourceType: form.sourceType,
    provider: form.provider.trim(),
    adapterKey: form.adapterKey.trim().toUpperCase(),
    integrationSettingKey: form.integrationSettingKey || null,
    sourceConfiguration,
    fieldMapping: parseJsonObject(form.fieldMapping, 'Field Mapping'),
    centerMapping: parseJsonObject(form.centerMapping, 'Center Mapping'),
    refreshMode: form.refreshMode,
    refreshCron: null,
    refreshTimezone: form.refreshTimezone.trim() || 'Asia/Kolkata',
    refreshConfiguration,
    ownerName: form.ownerName.trim() || null,
    ownerContact: form.ownerContact.trim() || null,
  };
}

function validationStepForms(value: unknown): ValidationStepForm[] {
  if (!Array.isArray(value)) return [];
  return value.map((raw, index) => {
    const step = objectValue(raw);
    const condition = String(step.condition || 'ALWAYS') as ValidationStepForm['condition'];
    return {
      name: String(step.name || `Validation tier ${index + 1}`),
      assigneeType: String(step.assigneeType).toUpperCase() === 'USER' ? 'USER' : 'ROLE',
      assigneeKey: String(step.assigneeKey || ''),
      condition,
      threshold: step.threshold === undefined || step.threshold === null ? '' : String(step.threshold),
    };
  });
}

function eligibilityFilterForms(value: unknown): EligibilityFilterForm[] {
  const filters = objectValue(value);
  return Object.entries(filters).map(([field, raw]) => {
    const rule = objectValue(raw);
    const configuredOperator = String(rule.operator || 'EQUALS').toUpperCase();
    const operator = ['EQUALS', 'IN', 'EXCLUDE_EXACT', 'IS_NOT_BLANK', 'IS_NOT_NULL'].includes(configuredOperator)
      ? configuredOperator as EligibilityFilterForm['operator']
      : 'EQUALS';
    return {
      field,
      operator,
      values: Array.isArray(rule.values)
        ? rule.values.map((item) => String(item)).join('\n')
        : String(rule.value ?? ''),
    };
  });
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function scheduleUnit(value: unknown): SourceForm['scheduleUnit'] {
  const unit = String(value || '').toUpperCase();
  return ['MINUTE', 'HOUR', 'DAY', 'WEEK', 'MONTH', 'YEAR'].includes(unit) ? unit as SourceForm['scheduleUnit'] : 'DAY';
}

function scheduleSummary(form: SourceForm) {
  return form.scheduleMode === 'EVERY_INTERVAL'
    ? `Every ${form.scheduleCount || '?'} ${form.scheduleUnit.toLowerCase()}(s), anchored at ${form.scheduleAnchorTime || '23:59'} IST`
    : `${form.scheduleCount || '?'} run(s) per ${form.scheduleUnit.toLowerCase()}, ending/anchored at ${form.scheduleAnchorTime || '23:59'} IST`;
}

function parseJsonObjectSafe(value: string): Record<string, unknown> {
  try { const parsed = JSON.parse(value || '{}'); return objectValue(parsed); } catch { return {}; }
}

function parseJsonObject(value: string, labelText: string): Record<string, unknown> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value || '{}');
  } catch {
    throw new Error(`${labelText} must contain valid JSON.`);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error(`${labelText} must be a JSON object.`);
  return parsed as Record<string, unknown>;
}

function prettyJson(value: Record<string, unknown>) {
  return JSON.stringify(value || {}, null, 2);
}

function label(value: string) {
  return value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function dateTime(value: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString('en-IN');
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}
